import { useSyncExternalStore } from 'react';

/**
 * Quem está usando o microfone agora (ditado, mensagem de voz). A chamada pelo nome
 * ("Deixar na mesa") ouve em qualquer tela, mas o Android dá o microfone a um só:
 * enquanto alguém segura, ela solta e espera.
 */
let holds = 0;
const listeners = new Set<() => void>();
/** Quem ouve a chamada solta o microfone por aqui antes de o outro começar. */
let yieldMic: (() => Promise<void>) | null = null;

function notify() {
  for (const listener of listeners) listener();
}

export function onMicYield(fn: (() => Promise<void>) | null): void {
  yieldMic = fn;
}

/**
 * Pede o microfone: a chamada pelo nome solta antes (até 600 ms). Devolve a função
 * que devolve o microfone (pode ser chamada mais de uma vez).
 */
export async function holdMic(): Promise<() => void> {
  holds += 1;
  notify();
  if (yieldMic) {
    await Promise.race([
      yieldMic().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, 600)),
    ]);
    // O Android solta o áudio um instante depois de a chamada pelo nome parar: sem essa
    // folga, o ditado abria "ouvindo" com o microfone ainda preso e não ouvia nada.
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    notify();
  };
}

export function micBusy(): boolean {
  return holds > 0;
}

export function useMicBusy(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    micBusy,
    () => false,
  );
}
